import "./styles.css";
import iconUrl from "../assets/icon.png?url";
import type { WorkerMessage } from "../worker/patcher.worker";
import { getStrings } from "./i18n";

interface FileSlot {
  file: File | null;
  data: ArrayBuffer | null;
}

type Phase = "idle" | "loading" | "running" | "done" | "error";

interface State {
  apk: FileSlot;
  obb: FileSlot;
  phase: Phase;
  progress: number;
  status: string;
  error: string | null;
  outputBlobUrl: string | null;
  outputFileName: string | null;
}

const t = getStrings();

const state: State = {
  apk: { file: null, data: null },
  obb: { file: null, data: null },
  phase: "idle",
  progress: 0,
  status: "",
  error: null,
  outputBlobUrl: null,
  outputFileName: null,
};

let worker: Worker | null = null;

export function mount(root: HTMLElement) {
  root.innerHTML = templateInitial();
  bindTopbar(root);
  bindCard(root, "apk", [".apk", "application/vnd.android.package-archive"]);
  bindCard(root, "obb", [".obb", "application/octet-stream"]);
  bindInstall(root);
  loadSponsor(root);
  render(root);
}

interface AdPayload {
  image_url?: string;
  click_url?: string;
  label?: string;
  html?: string;
  script_src?: string;
  target_html?: string;
  sponsor_label?: string;
}

async function loadSponsor(root: HTMLElement): Promise<void> {
  let r: Response;
  try {
    r = await fetch("/api/ad", { credentials: "omit" });
  } catch {
    return;
  }
  if (r.status !== 200) return;
  let payload: AdPayload;
  try {
    payload = (await r.json()) as AdPayload;
  } catch {
    return;
  }
  const slot = root.querySelector<HTMLElement>("[data-sponsor]");
  const tag = root.querySelector<HTMLElement>("[data-sponsor-tag]");
  const body = root.querySelector<HTMLElement>("[data-sponsor-body]");
  if (!slot || !tag || !body) return;

  if (payload.sponsor_label) tag.textContent = payload.sponsor_label;

  if (payload.image_url && payload.click_url) {
    const a = document.createElement("a");
    a.href = payload.click_url;
    a.target = "_blank";
    a.rel = "noopener noreferrer sponsored";
    const img = document.createElement("img");
    img.src = payload.image_url;
    img.alt = payload.label ?? "Sponsor";
    img.loading = "lazy";
    a.appendChild(img);
    body.appendChild(a);
    slot.classList.add("visible");
    return;
  }
  if (payload.html) {
    // Use createContextualFragment so any <script> tag in the embed
    // actually executes (innerHTML does not run scripts). Required for
    // most ad-network snippets (Adsterra, HilltopAds, Media.net, …).
    const range = document.createRange();
    range.selectNode(body);
    const fragment = range.createContextualFragment(payload.html);
    const wrap = document.createElement("div");
    wrap.className = "sponsor-html";
    wrap.appendChild(fragment);
    body.appendChild(wrap);
    slot.classList.add("visible");
    return;
  }
  if (payload.script_src) {
    const wrap = document.createElement("div");
    wrap.className = "sponsor-html";
    if (payload.target_html) {
      const range = document.createRange();
      range.selectNode(wrap);
      wrap.appendChild(range.createContextualFragment(payload.target_html));
    }
    const s = document.createElement("script");
    s.async = true;
    s.src = payload.script_src;
    wrap.appendChild(s);
    body.appendChild(wrap);
    slot.classList.add("visible");
  }
}

function templateInitial(): string {
  return `
    <div class="shell">
      <header class="topbar">
        <img src="${iconUrl}" alt="OBB Installer" />
        <div>
          <div class="title">OBB Installer</div>
          <div class="subtitle">${t.subtitle}</div>
        </div>
      </header>

      <section class="hero">
        <h1>${t.hero_title}</h1>
        <p>${t.hero_body}</p>
      </section>

      <section class="card empty" data-card="apk">
        <div class="card-head">
          <div class="badge">A</div>
          <div class="meta">
            <strong>${t.card_apk}</strong>
            <span class="status">${t.no_file_selected}</span>
          </div>
        </div>
        <div class="dropzone" data-drop="apk">
          <div class="label">📁 ${t.drop_zone_apk}</div>
          <div class="format">${t.format_apk}</div>
          <input type="file" accept=".apk,application/vnd.android.package-archive" hidden data-input="apk" />
        </div>
      </section>

      <section class="card empty" data-card="obb">
        <div class="card-head">
          <div class="badge">O</div>
          <div class="meta">
            <strong>${t.card_obb}</strong>
            <span class="status">${t.no_file_selected}</span>
          </div>
        </div>
        <div class="dropzone" data-drop="obb">
          <div class="label">📁 ${t.drop_zone_obb}</div>
          <div class="format">${t.format_obb}</div>
          <input type="file" accept=".obb,application/octet-stream" hidden data-input="obb" />
        </div>
      </section>

      <section class="install">
        <button data-install disabled>
          <span data-install-label>${t.install_apk}</span>
        </button>
        <div class="subtitle" data-subtitle></div>
        <div class="progress" hidden data-progress></div>
        <div class="status-line" data-status></div>
        <div data-output></div>
      </section>

      <aside class="sponsor" data-sponsor>
        <span class="sponsor-tag" data-sponsor-tag>Sponsorisé</span>
        <div data-sponsor-body></div>
      </aside>

      <section class="info">
        <div class="icon">i</div>
        <div>
          <h3>${t.how_it_works_title}</h3>
          <p>${t.how_it_works_body}</p>
        </div>
      </section>

      <section class="info warn">
        <div class="icon">!</div>
        <div>
          <h3>${t.caveat_title}</h3>
          <p>${t.caveat_body}</p>
        </div>
      </section>

      <footer>
        <a href="https://github.com/aciderix/APK-OBB-HELPER/releases/latest">${t.android_app_link}</a>
        ·
        <a href="https://github.com/aciderix/Apk-obb-web">${t.source_link}</a>
      </footer>
    </div>
  `;
}

function bindTopbar(_root: HTMLElement) {
  // Reserved for future settings / language toggle.
}

function bindCard(root: HTMLElement, kind: "apk" | "obb", _accepts: string[]) {
  const dz = root.querySelector<HTMLDivElement>(`[data-drop="${kind}"]`)!;
  const input = root.querySelector<HTMLInputElement>(`[data-input="${kind}"]`)!;
  dz.addEventListener("click", () => input.click());
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    if (f) onFile(kind, f, root);
  });
  ["dragenter", "dragover"].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      e.preventDefault();
      dz.classList.add("drag");
    })
  );
  ["dragleave", "drop"].forEach((ev) =>
    dz.addEventListener(ev, (e) => {
      e.preventDefault();
      dz.classList.remove("drag");
    })
  );
  dz.addEventListener("drop", (e) => {
    const f = (e as DragEvent).dataTransfer?.files?.[0];
    if (f) onFile(kind, f, root);
  });
}

async function onFile(kind: "apk" | "obb", f: File, root: HTMLElement) {
  state[kind].file = f;
  state.phase = "loading";
  state.status = "";
  state.error = null;
  render(root);
  try {
    const buf = await f.arrayBuffer();
    state[kind].data = buf;
    state.phase = "idle";
  } catch (e) {
    state.error = String(e);
    state.phase = "error";
  }
  render(root);
}

function bindInstall(root: HTMLElement) {
  const btn = root.querySelector<HTMLButtonElement>("[data-install]")!;
  btn.addEventListener("click", () => {
    if (state.phase === "done" || state.phase === "error") {
      reset(root);
      return;
    }
    if (!state.apk.data) return;
    runWorker(root);
  });
}

function reset(root: HTMLElement) {
  state.apk = { file: null, data: null };
  state.obb = { file: null, data: null };
  state.phase = "idle";
  state.progress = 0;
  state.status = "";
  state.error = null;
  if (state.outputBlobUrl) URL.revokeObjectURL(state.outputBlobUrl);
  state.outputBlobUrl = null;
  state.outputFileName = null;
  // Clear file inputs
  root.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach((i) => (i.value = ""));
  render(root);
}

function runWorker(root: HTMLElement) {
  state.phase = "running";
  state.progress = 0;
  state.status = t.phase_running;
  state.error = null;
  if (state.outputBlobUrl) URL.revokeObjectURL(state.outputBlobUrl);
  state.outputBlobUrl = null;
  state.outputFileName = null;
  render(root);

  if (worker) worker.terminate();
  worker = new Worker(new URL("../worker/patcher.worker.ts", import.meta.url), {
    type: "module",
  });
  worker.addEventListener("message", (e: MessageEvent<WorkerMessage>) => {
    const m = e.data;
    if (m.type === "progress") {
      state.progress = m.progress;
      state.status = m.status;
      render(root);
    } else if (m.type === "done") {
      const blob = new Blob([m.apk], { type: "application/vnd.android.package-archive" });
      state.outputBlobUrl = URL.createObjectURL(blob);
      state.outputFileName = `${m.packageName}.patched.apk`;
      state.phase = "done";
      state.status = t.phase_done;
      render(root);
    } else if (m.type === "error") {
      state.phase = "error";
      state.error = m.message;
      render(root);
    }
  });
  worker.postMessage(
    {
      type: "run",
      apk: state.apk.data,
      obb: state.obb.data && state.obb.file
        ? { name: state.obb.file.name, data: state.obb.data }
        : null,
    },
    [
      state.apk.data!,
      ...(state.obb.data ? [state.obb.data] : []),
    ]
  );
  // After transfer the buffers are detached on this side; null them out.
  state.apk.data = null;
  state.obb.data = null;
}

function render(root: HTMLElement) {
  // APK card
  updateCard(root, "apk", state.apk.file);
  updateCard(root, "obb", state.obb.file);

  // Install button
  const btn = root.querySelector<HTMLButtonElement>("[data-install]")!;
  const label = root.querySelector<HTMLElement>("[data-install-label]")!;
  const subtitle = root.querySelector<HTMLElement>("[data-subtitle]")!;
  const progressEl = root.querySelector<HTMLDivElement>("[data-progress]")!;
  const statusEl = root.querySelector<HTMLDivElement>("[data-status]")!;
  const outputEl = root.querySelector<HTMLDivElement>("[data-output]")!;

  const hasApk = !!state.apk.file;
  const hasObb = !!state.obb.file;
  const running = state.phase === "running" || state.phase === "loading";
  btn.disabled = !hasApk || running;
  btn.classList.toggle("running", running);

  if (state.phase === "done" || state.phase === "error") {
    label.textContent = t.reset;
  } else if (running) {
    label.textContent = t.phase_running;
  } else {
    label.textContent = hasObb ? t.install_apk_obb : t.install_apk;
  }

  subtitle.textContent = state.phase === "idle" && hasObb ? t.install_subtitle : "";

  progressEl.hidden = !running;
  progressEl.style.setProperty("--p", `${(state.progress * 100).toFixed(0)}%`);

  statusEl.textContent =
    state.phase === "error" && state.error
      ? t.phase_error(state.error)
      : state.status;
  statusEl.classList.toggle("error", state.phase === "error");

  // Output download link
  if (state.phase === "done" && state.outputBlobUrl && state.outputFileName) {
    outputEl.innerHTML = `
      <a class="download" href="${state.outputBlobUrl}" download="${state.outputFileName}">
        ⬇ ${t.download_apk(state.outputFileName.replace(".patched.apk", ""))}
      </a>
    `;
  } else {
    outputEl.innerHTML = "";
  }
}

function updateCard(root: HTMLElement, kind: "apk" | "obb", file: File | null) {
  const card = root.querySelector<HTMLDivElement>(`[data-card="${kind}"]`)!;
  const status = card.querySelector<HTMLElement>(".status")!;
  if (file) {
    card.classList.remove("empty");
    card.classList.add("selected");
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    status.textContent = t.selected_label(file.name, sizeMb);
  } else {
    card.classList.add("empty");
    card.classList.remove("selected");
    status.textContent = t.no_file_selected;
  }
}
